import {AfterViewInit, Component, OnDestroy, OnInit} from "@angular/core";
import {AutoSaveService} from '../../services/auto-save.service';
import {UserService} from '../../services/user.service';
import {JuconnUserRoleEnum} from '../../models/juconn-user-role.enum';
import {Dialog} from "@angular/cdk/dialog";
import {SerialNumberModalComponent} from "../../components/serial-number-modal/serial-number-modal.component";
import {RawDataModalComponent} from "../../components/raw-data-modal/raw-data-modal.component";
import {ShareService} from "../../services/share.service";
import {environment} from "../../../../../environments/environment";
import {Subscription} from "rxjs";
import {filter} from "rxjs/operators";
import {ValidationService} from "../../services/validation.service";
import {NavigationEnd, Router} from "@angular/router";
import {EditOwnPasswordComponent} from "../../components/edit-own-password/edit-own-password.component";

@Component({
  selector: "app-root-page",
  templateUrl: "./root-page.component.html",
  styleUrls: ["./root-page.component.scss"],
})
export class RootPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly tabs = {
    general: 0,
    building: 1,
    heating: 2,
    sensors: 3,
    photos: 4,
  };

  public showMenu: boolean = false;
  public applicationTitle = environment.envTarget;

  public gatewayId: string;

  public currentStep: number = 0;

  private subscriptions: Subscription[] = [];

  public constructor(
    private userService: UserService,
    private dialog: Dialog,
    public shareService: ShareService,
    public autoSaveService: AutoSaveService,
    public validationService: ValidationService,
    private router: Router,
  ) {
    this.gatewayId = this.shareService.getGatewayId();
    this.subscriptions.push(this.shareService.gatewayId$.subscribe(id => {
      this.gatewayId = id;
    }));
    this.subscriptions.push(
      this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
        this.currentStep = this.resolveCurrentStep();
      })
    );
    this.currentStep = this.resolveCurrentStep();
  }

  private resolveCurrentStep(): number {
    const segment = this.router.url.split('/')[2];
    return segment !== undefined ? this.tabs[segment] : 0;
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => window.scrollTo(0, 1), 0);
  }

  async ngOnInit() {
    document.addEventListener('gesturestart', function (e) {
      e.preventDefault();
    });

    this.shareService.checkSignalQuality();
  }

  public async onRefreshSignalQualityClicked() {
    await this.shareService.checkSignalQuality();
  }

  public async onGetRawDataClicked() {
    this.dialog.open(RawDataModalComponent, {data: {gatewayId: this.gatewayId}});
  }

  public async onGetSerialNumberClicked() {
    this.dialog.open(SerialNumberModalComponent, {data: {serialNumber: this.gatewayId}})
      .closed.subscribe(async (result: { serialNumber: string; resetData?: boolean }) => {
      if (result) {
        if (result.resetData) {
          await this.shareService.setGatewayIdWithReset(result.serialNumber);
        } else {
          await this.shareService.setGatewayId(result.serialNumber);
        }
      }
    });
  }


  async onSaveDebugDataClick() {
    await this.autoSaveService.saveDebugData();
  }

  async onLoadDebugDataClick() {
    await this.autoSaveService.loadDebugData();
  }

  async onClearDebugDataClick() {
    await this.autoSaveService.clearDebugData();
  }

  canAccessPlannerTool(): boolean {
    return this.userService.user.roles.includes(JuconnUserRoleEnum.super_admin) ||
      this.userService.user.roles.includes(JuconnUserRoleEnum.user_planner) ||
      this.userService.user.roles.includes(JuconnUserRoleEnum.user_service_technician);
  }

  getGeneralComponentStepClass(): string {
    return this.validationService.isGeneralValid(this.shareService.generalGateway, this.shareService.generalMeasurement, this.shareService.generalPrePhotos, this.shareService.generalSending) ? 'completed' : '';
  }

  getBuildingComponentStepClass(): string {
    return this.validationService.isBuildingValid(this.shareService.building) ? 'completed' : '';
  }

  getHeatingComponentStepClass(): string {
    return this.validationService.isHeatingValid(this.shareService.heatingCheck, this.shareService.heatingInformation) ? 'completed' : '';
  }

  getSensorsComponentStepClass(): string {
    return this.validationService.isSensorsValid(this.shareService.generalGateway?.uvsModules, this.shareService.sensorsCounter, this.shareService.sensorsOther, this.shareService.sensorsOutsideTemp, this.shareService.sensorsPump, this.shareService.sensorsTemperature) ? 'completed' : '';
  }

  getPhotosComponentStepClass(): string {
    return this.validationService.isPhotosValid(this.shareService.photos, this.shareService.generalPrePhotos?.preInstallHeatingImages.length) ? 'completed' : '';
  }

  getGeneralComponentStepStyle(): string {
    return this.currentStep > 0 && !this.validationService.isGeneralValid(this.shareService.generalGateway, this.shareService.generalMeasurement, this.shareService.generalPrePhotos, this.shareService.generalSending) ? 'background-color: red' : '';
  }

  getBuildingComponentStepStyle(): string {
    return this.currentStep > 1 && !this.validationService.isBuildingValid(this.shareService.building) ? 'background-color: red' : '';
  }

  getHeatingComponentStepStyle(): string {
    return this.currentStep > 2 && !this.validationService.isHeatingValid(this.shareService.heatingCheck, this.shareService.heatingInformation) ? 'background-color: red' : '';
  }

  getSensorsComponentStepStyle(): string {
    return this.currentStep > 3 && !this.validationService.isSensorsValid(this.shareService.generalGateway?.uvsModules, this.shareService.sensorsCounter, this.shareService.sensorsOther, this.shareService.sensorsOutsideTemp, this.shareService.sensorsPump, this.shareService.sensorsTemperature) ? 'background-color: red' : '';
  }

  getPhotosComponentStepStyle(): string {
    return this.currentStep === 4 && !this.validationService.isPhotosValid(this.shareService.photos, this.shareService.generalPrePhotos?.preInstallHeatingImages.length) ? 'background-color: red' : '';
  }

  openOwnPasswordPopup() {
    this.dialog.open(EditOwnPasswordComponent, {});
  }

}
